import { useMemo } from "react";
import { Building2Icon } from "lucide-react";

import { Row, Text } from "@/components";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import {
  companyMatchTone,
  useCustomerCompanyMatches,
} from "@/hooks/useCustomerCompanyMatches";
import { COMPANY_MATCH_REASONS } from "@/lib/companies";
import { cn } from "@/lib/utils";

export const SearchV2SameCompanyBadge = ({
  workedAtCompanies,
  isCurrentLaborupPlacement,
}: {
  workedAtCompanies: string[] | null;
  isCurrentLaborupPlacement?: boolean;
}) => {
  /* React Hooks */
  const { findMatches } = useCustomerCompanyMatches();

  const matchedCompany = useMemo(
    // Show the highest ranked match
    () => findMatches(workedAtCompanies ?? [])?.[0],
    [findMatches, workedAtCompanies],
  );

  if (!matchedCompany) return null;

  /* Helpers */
  const isCurrentCustomer =
    matchedCompany.reason === COMPANY_MATCH_REASONS.CURRENT_CUSTOMER;

  const getReason = () => {
    // Red reasons (same company / currently at a customer) always win over
    // the placement relabel.
    if (
      isCurrentLaborupPlacement &&
      matchedCompany.reason === COMPANY_MATCH_REASONS.OTHER
    ) {
      return COMPANY_MATCH_REASONS.CURRENT_PLACEMENT;
    }
    return matchedCompany.reason;
  };

  /* Render */
  const tone = companyMatchTone(
    matchedCompany.reason,
    isCurrentLaborupPlacement,
  );

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Row
            className={cn(
              "h-5 shrink-0 items-center gap-1.5 rounded-full border px-2",
              tone.container,
            )}
          >
            <Building2Icon className={cn("h-3 w-3", tone.content)} />
            <Text
              className={cn("text-xs font-medium leading-none", tone.content)}
            >
              {isCurrentLaborupPlacement || isCurrentCustomer
                ? "Works"
                : "Worked"}{" "}
              at {matchedCompany.company}
            </Text>
          </Row>
        </TooltipTrigger>
        <TooltipContent side="top">
          <Text className="text-xs font-normal text-slate-700">
            {getReason()}
          </Text>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
